import React, { useState, useEffect, useRef } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { calculateJobTotalsLegacy } from "@/dentlab/src/lib/utils/calculate";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { FileText } from "lucide-react";
import { cn } from "@/dentlab/src/lib/util";

interface TotalRow {
	name: string;
	value: string;
	raw: number;
	bold?: boolean;
	borderBottom?: boolean;
}

const DiscountInput: React.FC<{
	field: "discount_work" | "discount_material";
	label: string;
	documentId: number;
}> = ({ field, label, documentId }) => {
	const storeValue = useJobStore(
		(s) => s.jobDocuments[documentId]?.[field] ?? 0
	);
	const updateDocumentDiscount = useJobStore((s) => s.updateDocumentDiscount);
	const [localValue, setLocalValue] = useState(String(storeValue));
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		setLocalValue(String(storeValue));
	}, [storeValue]);

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	return (
		<div className="flex items-center justify-between gap-2">
			<label className="text-sm text-muted-foreground whitespace-nowrap">
				{label}
			</label>
			<div className="flex items-center gap-1">
				<input
					type="number"
					value={localValue}
					onChange={(e) => {
						setLocalValue(e.target.value);
						if (timerRef.current) clearTimeout(timerRef.current);
						timerRef.current = setTimeout(() => {
							const clamped = Math.min(
								Math.max(parseFloat(e.target.value) || 0, 0),
								100
							);
							setLocalValue(String(clamped));
							updateDocumentDiscount(documentId, field, clamped);
						}, 700);
					}}
					className="w-16 text-right text-sm border rounded px-1 py-0.5"
					data-testid={`total-tab-${field}`}
				/>
				<span className="text-sm text-muted-foreground">%</span>
			</div>
		</div>
	);
};

export const TotalTab: React.FC = () => {
	const { job, jobItemsForDocuments, selectedDocumentId } = useJobStore(
		(state) => ({
			job: state.job,
			jobItemsForDocuments: state.jobItemsForDocuments,
			selectedDocumentId: state.selectedDocumentId,
		})
	);

	const { jobDocuments } = useJobStore((state) => ({
		jobDocuments: state.jobDocuments,
	}));

	const { organization, client } = useCentralStore((state) => ({
		organization: state.organization,
		client: state.client,
	}));

	if (!selectedDocumentId || !jobDocuments[selectedDocumentId]) {
		return (
			<div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 p-4">
				<FileText className="h-8 w-8" />
				<span className="text-sm">Kein Dokument ausgewählt</span>
			</div>
		);
	}

	if (!job || !organization || !client) return null;

	const jobDocument = jobDocuments[selectedDocumentId];
	const jobItems =
		(jobItemsForDocuments?.[selectedDocumentId] as JobItemEntityType[]) ??
		[];

	const {
		total,
		mwst,
		amountMaterials,
		amountWorkAndFixedRate,
		deductions,
		taxpunkte,
		amountPostage,
		amountExternalWork,
		deductionsWorkAndFixedRate,
		deductionsMaterials,
		subTotal,
		mwstPercentage,
		akontoReallocation,
		roundingDifference,
	} = calculateJobTotalsLegacy({
		job,
		jobDocument,
		jobItems,
		organization,
		client,
	});

	const rows: TotalRow[] = [
		{
			name: "Arbeit",
			value: amountWorkAndFixedRate.text,
			raw: amountWorkAndFixedRate.value,
			bold: true,
		},
		{ name: "Taxpunkte", value: taxpunkte.text, raw: taxpunkte.value },
		{
			name: "Material",
			value: amountMaterials.text,
			raw: amountMaterials.value,
			bold: true,
		},
		{
			name: "Fremdarbeiten",
			value: amountExternalWork.text,
			raw: amountExternalWork.value,
		},
		{
			name: "Porto",
			value: amountPostage.text,
			raw: amountPostage.value,
			borderBottom: true,
		},
		{
			name: "Abzüge Arbeit",
			value: deductionsWorkAndFixedRate.text,
			raw: deductionsWorkAndFixedRate.value,
		},
		{
			name: "Abzüge Material",
			value: deductionsMaterials.text,
			raw: deductionsMaterials.value,
		},
		{
			name: "Abzüge Gesamt",
			value: deductions.text,
			raw: deductions.value,
			bold: true,
			borderBottom: true,
		},
		{ name: "Subtotal", value: subTotal.text, raw: subTotal.value },
		{
			name: `MwSt. (${mwstPercentage}%)`,
			value: mwst.text,
			raw: mwst.value,
		},
	];

	if (akontoReallocation && akontoReallocation.value > 0) {
		rows.push({
			name: "Akonto",
			value: `-${akontoReallocation.text}`,
			raw: akontoReallocation.value,
		});
	}

	if (roundingDifference && roundingDifference.value !== 0) {
		rows.push({
			name: "Rundungsdifferenz",
			value: roundingDifference.text,
			raw: roundingDifference.value,
			borderBottom: true,
		});
	}

	rows.push({
		name: "Total",
		value: total.text,
		raw: total.value,
		bold: true,
	});

	const visibleRows = rows.filter((row) => row.bold || row.raw !== 0);

	return (
		<div className="p-4" data-testid="total-tab">
			<table className="w-full text-sm">
				<tbody>
					{visibleRows.map((row) => (
						<tr
							key={row.name}
							className={cn(
								row.borderBottom && "border-b border-gray-200"
							)}
						>
							<td
								className={cn(
									"py-1 pr-4",
									row.bold && "font-semibold",
									row.name === "Total" && "text-base pt-2"
								)}
							>
								{row.name}
							</td>
							<td
								className={cn(
									"py-1 text-right",
									row.bold && "font-semibold",
									row.name === "Total" && "text-base pt-2"
								)}
							>
								{row.value}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
				<DiscountInput
					field="discount_work"
					label="Rabatt Tarif"
					documentId={selectedDocumentId}
				/>
				<DiscountInput
					field="discount_material"
					label="Rabatt Material"
					documentId={selectedDocumentId}
				/>
			</div>
		</div>
	);
};
